import { useEffect, useState } from "react";
import { MessageSquare, Send, X, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import "../admin-affiliate-premium.css";

function statusPill(s) {
  const map = {
    open: { c: "#fcd34d", b: "rgba(244,180,49,0.35)", g: "rgba(244,180,49,0.12)" },
    in_progress: { c: "#93c5fd", b: "rgba(59,130,246,0.35)", g: "rgba(59,130,246,0.12)" },
    answered: { c: "#6ee7b7", b: "rgba(16,185,129,0.35)", g: "rgba(16,185,129,0.12)" },
    closed: { c: "#cbd5e1", b: "rgba(148,163,184,0.32)", g: "rgba(148,163,184,0.10)" },
  };
  return map[s] || map.open;
}

export default function AffiliateTickets() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("");
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");

  const load = async () => {
    try {
      const r = await api.get("/admin/aff-tickets" + (filter ? `?status=${filter}` : ""));
      setRows(r.data?.rows || []);
      setCounts(r.data?.counts || {});
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  useEffect(() => { load(); }, [filter]);

  const send = async () => {
    if (!active || !reply) return;
    try {
      const r = await api.post(`/admin/aff-tickets/${active.id}/reply`, { message: reply });
      setActive(r.data?.ticket); setReply("");
      load();
      toast.success("Reply sent");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const setStatus = async (s) => {
    if (!active) return;
    try { await api.patch(`/admin/aff-tickets/${active.id}`, { status: s }); toast.success(`Status: ${s}`); load(); setActive({ ...active, status: s }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="ap-bg p-4 lg:p-6">
      <div className="flex items-end justify-between mb-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.32em] text-amber-300 font-bold mb-1">CONCIERGE</div>
          <div className="text-3xl font-black ap-gold-text">Affiliate Tickets</div>
          <div className="text-white/55 text-sm mt-1">Reply to affiliate support requests and manage status.</div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {["", "open", "in_progress", "answered", "closed"].map((s) => (
            <button key={s || "all"} onClick={() => setFilter(s)} className={`px-3 h-8 rounded-lg text-[11px] font-bold uppercase tracking-[0.18em] ${filter === s ? "ap-btn-gold" : "ap-btn-ghost"}`}>
              {s || "All"} {s && counts[s] != null ? `(${counts[s]})` : ""}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-5">
          {rows.length === 0 ? (
            <div className="ap-glass p-10 text-center text-white/45">No tickets here.</div>
          ) : (
            <div className="space-y-2">
              {rows.map((t) => {
                const sp = statusPill(t.status);
                const isActive = active?.id === t.id;
                return (
                  <button key={t.id} onClick={() => setActive(t)} className="w-full text-left p-4 rounded-xl transition-all" style={{ background: isActive ? "rgba(212,175,55,0.10)" : "rgba(255,255,255,0.02)", border: `1px solid ${isActive ? "rgba(212,175,55,0.55)" : "rgba(255,255,255,0.08)"}` }}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-bold text-white truncate">{t.subject}</div>
                      <span className="text-[10px] font-bold uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-md" style={{ color: sp.c, background: sp.g, border: `1px solid ${sp.b}` }}>{t.status}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[11px] text-white/55">{t.username}</span>
                      <span className="text-[11px] text-white/35">• {t.priority}</span>
                      <span className="ml-auto text-[11px] text-white/45">{(t.updated_at || "").slice(0, 10)}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div className="lg:col-span-7">
          {active ? (
            <div className="ap-glass" style={{ padding: 18 }}>
              <div className="flex items-center justify-between gap-3 mb-3 pb-3" style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 font-bold">{active.username} • {active.email}</div>
                  <div className="font-black text-white text-lg truncate">{active.subject}</div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => setStatus("in_progress")} className="ap-btn-ghost px-2 h-8 rounded-md text-[10px] font-bold uppercase tracking-[0.18em]">In progress</button>
                  <button onClick={() => setStatus("closed")} className="ap-btn-ghost px-2 h-8 rounded-md text-[10px] font-bold uppercase tracking-[0.18em] text-rose-300">Close</button>
                  <button onClick={() => setActive(null)} className="ap-btn-ghost p-2 rounded-md"><X size={14} /></button>
                </div>
              </div>
              <div className="space-y-3 max-h-[440px] overflow-y-auto pr-1">
                {(active.messages || []).map((m) => (
                  <div key={m.id} className={`flex ${m.sender === "admin" ? "justify-end" : "justify-start"}`}>
                    <div className="max-w-[80%] p-3 rounded-xl" style={{ background: m.sender === "admin" ? "linear-gradient(135deg, rgba(212,175,55,0.18), rgba(160,125,46,0.06))" : "rgba(255,255,255,0.04)", border: `1px solid ${m.sender === "admin" ? "rgba(212,175,55,0.4)" : "rgba(255,255,255,0.08)"}` }}>
                      <div className="text-[10px] font-bold uppercase tracking-[0.22em] mb-1" style={{ color: m.sender === "admin" ? "#f4e9a1" : "#93c5fd" }}>
                        {m.sender === "admin" ? "Concierge" : "Affiliate"} • {(m.created_at || "").slice(11, 16)}
                      </div>
                      <div className="text-white text-[13.5px] whitespace-pre-wrap">{m.body}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-end gap-2 mt-4 pt-3" style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
                <textarea rows={2} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Type your reply…" className="flex-1 ap-input resize-none" data-testid="admin-ticket-reply-input" />
                <button onClick={send} className="ap-btn-gold px-4 h-11 rounded-xl text-[12px] font-bold inline-flex items-center gap-2" data-testid="admin-ticket-reply-send"><Send size={14} /> Send</button>
              </div>
            </div>
          ) : (
            <div className="ap-glass p-10 text-center text-white/45">Select a ticket to view conversation.</div>
          )}
        </div>
      </div>
    </div>
  );
}
